import { useCallback, useMemo, useState, type ReactNode } from "react";
import { CustomerSource } from "@omnabase/ui";
import type { Customer } from "@omnabase/shared";
import { licencesOf, licencesRowValue, readSourceOpen, sourceSections, writeSourceOpen, type SourcePlace, type SourceSection } from "../../lib/customerSources";
import type { Facts } from "../../lib/integrations";
import { localStore } from "../../lib/localStore";
import { useSession } from "../../shell/store";

/** The customer sources panel round (2026-11-04, brief `docs/briefs/customer-sources-panel-round.md`): the sections the ticket sidebar's Profile
 *  tab and the customer page draw, one per source (`lib/customerSources.ts` builds them), through the kit's `CustomerSource`. Each section's
 *  fold is the person's and is remembered by the browser per place and source; the Information row "Licenses" opens the first section that
 *  holds licences and brings its Licences group into view (no animation: a jump and the keyboard's focus, finding 15). */
export type SourcePanel = { sections: SourceSection[]; licences: Customer["licenses"]; folds: SourceFolds; /** the Information row's words */ rowWords: string };
/** the panel's reads put together: the lookup (`facts`, still `loading` or not) and the customer read, in the workspace's project order */
export function useSourcePanel(o: { slug: string; place: SourcePlace; facts: { data: Facts | null; loading: boolean }; customer: { licenses: Customer["licenses"] } | null | undefined; ticketProjectId?: string | null }): SourcePanel {
  const { workspace } = useSession();
  const folds = useSourceFolds(o.slug, o.place);
  const licences = licencesOf(o.customer, o.facts.data);
  const projects = useMemo(() => workspace.projects.map((p) => p.id), [workspace.projects]);
  const sections = useMemo(() => sourceSections({ facts: o.facts.data, licences, projects, ticketProjectId: o.ticketProjectId ?? null }), [o.facts.data, licences, projects, o.ticketProjectId]);
  return { sections, licences, folds, rowWords: licencesRowValue(licences, o.facts.loading) };
}

export type SourceFolds = { isOpen: (key: string) => boolean; set: (key: string, open: boolean) => void; reveal: (section: SourceSection) => void };

export function useSourceFolds(slug: string, place: SourcePlace): SourceFolds {
  const [overlay, setOverlay] = useState<Record<string, boolean>>({}); // what this screen set, also when the browser keeps nothing
  const isOpen = useCallback((key: string) => overlay[key] ?? readSourceOpen(localStore(), slug, place, key), [overlay, slug, place]);
  const set = useCallback((key: string, open: boolean) => {
    if (isOpen(key) === open) return;
    writeSourceOpen(localStore(), slug, place, key, open);
    setOverlay((o) => ({ ...o, [key]: open }));
  }, [isOpen, slug, place]);
  const reveal = useCallback((s: SourceSection) => { set(s.key, true); requestAnimationFrame(() => showLicences(s.anchor)); }, [set]);
  return { isOpen, set, reveal };
}

/** the Licences group on screen and its label focused (the group sits inside the section, open since the frame before) */
function showLicences(anchor: string): void {
  const group = document.getElementById(`licences-${anchor}`);
  if (!group) return;
  group.scrollIntoView({ block: "start" });
  group.querySelector<HTMLElement>(".ob-customer-source__label")?.focus({ preventScroll: true });
}

export function CustomerSources({ sections, folds, foot }: { sections: SourceSection[]; folds: SourceFolds; /** under the live source's links (the customer page's "Fields") */ foot?: ReactNode }) {
  return (
    <>
      {sections.map((s) => (
        <CustomerSource key={s.key} title={s.title} host={s.host} digest={s.digest} anchor={s.anchor} open={folds.isOpen(s.key)} onToggle={(open) => folds.set(s.key, open)}
          fields={s.fields} licences={s.licences} subscriptions={s.subscriptions} orders={s.orders} account={s.account} links={s.links} foot={s.live ? foot : undefined} />
      ))}
    </>
  );
}

/** the Information row "Licenses" as a quiet button that opens the first section holding licences; plain words while there is none */
export function LicencesRowValue({ words, sections, folds }: { words: string; sections: SourceSection[]; folds: SourceFolds }): ReactNode {
  const first = sections.find((s) => s.licences.length > 0);
  return first ? <button type="button" className="ob-info__open-licences" onClick={() => folds.reveal(first)}>{words}</button> : words;
}
